'use client';

import React, { useEffect, useState } from 'react';
import HeroSection from './HeroSection';
import CompanyStory from './CompanyStory';
// import IndustryRecognition from './IndustryRecognition';
import ClientSuccessPhilosophy from './ClientSuccessPhilosophy';
import CTASection from './CTASection';

interface Milestone {
  year: string;
  title: string;
  description: string;
  icon: string;
}

interface Certification {
  name: string;
  issuer: string;
  year: string;
  logo: string;
  alt: string;
}

interface Partnership {
  name: string;
  description: string;
  logo: string;
  alt: string;
}

interface Achievement {
  title: string;
  description: string;
  icon: string;
  metric?: string;
}

interface PhilosophyPillar {
  title: string;
  description: string;
  icon: string;
  principles: string[];
}

const AboutInteractive: React.FC = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const heroData = {
    title: "Medical Expertise Meets Business Excellence",
    subtitle: "About Total Medical Practice Support",
    description: "We are healthcare industry specialists dedicated to transforming medical operations through deep clinical understanding, regulatory mastery, and proven business strategies that deliver measurable results."
  };

  const companyData = {
    story: "Founded by healthcare professionals who experienced firsthand the operational challenges facing medical organizations, Total Medical Practice Support was born from a simple truth: generic business consulting fails in healthcare. Our founders—seasoned medical administrators, certified coders, and compliance experts—recognized that healthcare organizations needed consultants who truly understood HIPAA complexities, medical coding nuances, and the unique staffing challenges of clinical environments. Today, we stand as the trusted partner for healthcare leaders seeking operational excellence without compromising patient care quality.",
    mission: "To empower healthcare organizations with specialized consulting services that combine deep medical industry expertise with proven business strategies, enabling operational excellence while maintaining the highest standards of patient care and regulatory compliance.",
    vision: "To be the definitive authority in healthcare operational consulting, recognized across the industry for transforming complex medical business challenges into sustainable competitive advantages through unmatched expertise and measurable results.",
    milestones: [
    {
      year: "Step 1",
      title: "The \"Vital Signs\" Triage",
      description: "We start by listening. You define the narrative—whether you need assistance with a small, specific task or a large-scale organizational challenge. We support over 15 different lines of service, scaling our expertise to match the exact size and scope of the problem you describe.",
      icon: "HeartIcon"
    },
    {
      year: "Step 2",
      title: "Scope Definition",
      description: "We translate your stated needs into a clear project framework. We establish the specific parameters and objectives of the engagement—distinguishing, for example, between a single contract review versus a full department restructure. This ensures the upcoming proposal targets your precise goals with zero ambiguity.",
      icon: "MagnifyingGlassIcon"
    },
    {
      year: "Step 3",
      title: "Internal Resource Matching",
      description: "We identify which of our Subject Matter Experts is required for your specific scope—whether you need a high-level Compliance Officer or a tactical Coding Specialist—to ensure we have the availability to handle the request.",
      icon: "ChartBarIcon"
    },
    {
      year: "Step 4",
      title: "The Care Plan & Proposal",
      description: "We present the solution and the agreement in a single step. We outline the high-level roadmap of how we will solve your challenge, paired with a formal Statement of Work (SOW) detailing the deliverables, timeline, and investment. This acts as the prescription; once signed, the real work begins.",
      icon: "DocumentTextIcon"
    },
    {
      year: "Step 5",
      title: "Deployment & Treatment",
      description: "Project Commencement. This is where the diagnosis and analysis actually happen. We deploy the assigned team, open the secure channels, and begin the work of fixing the root causes while you get back to running your business.",
      icon: "PresentationChartLineIcon"
    }] as
    Milestone[]
  };

  const certifications: Certification[] = [
  {
    name: "HIPAA Compliance Certification",
    issuer: "Healthcare Compliance Association",
    year: "2024",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1ee89a7e5-1764672209990.png",
    alt: "HIPAA compliance certification badge with shield and checkmark symbol"
  },
  {
    name: "AAPC Certified Coding",
    issuer: "American Academy of Professional Coders",
    year: "2024",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_11cdcef4b-1765207187502.png",
    alt: "AAPC professional coding certification emblem with medical symbols"
  },
  {
    name: "MGMA Membership",
    issuer: "Medical Group Management Association",
    year: "2024",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_18e83ebc8-1765207188270.png",
    alt: "MGMA healthcare management association membership badge"
  },
  {
    name: "HFMA Certification",
    issuer: "Healthcare Financial Management Association",
    year: "2024",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1fbfb09dd-1765207187136.png",
    alt: "HFMA financial management certification seal with professional emblem"
  }];


  const partnerships: Partnership[] = [
  {
    name: "American Medical Association",
    description: "Strategic partnership providing access to latest clinical practice guidelines and healthcare policy updates",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1e39471d7-1765129016805.png",
    alt: "American Medical Association partnership logo with medical caduceus symbol"
  },
  {
    name: "Healthcare Information Management Systems Society",
    description: "Collaboration on healthcare IT best practices and digital transformation initiatives",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_13d7bb0c4-1765207186819.png",
    alt: "HIMSS healthcare technology partnership emblem with digital health symbols"
  },
  {
    name: "National Association of Healthcare Access Management",
    description: "Joint initiatives improving patient access and revenue cycle operations",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_17706cb30-1765207188159.png",
    alt: "NAHAM healthcare access management partnership badge"
  }];


  const achievements: Achievement[] = [
  {
    title: "Healthcare Organizations Served",
    description: "Trusted partner to medical practices, hospitals, and health systems nationwide",
    icon: "BuildingOffice2Icon",
    metric: "500+"
  },
  {
    title: "Client Satisfaction Rate",
    description: "Consistently exceeding expectations with measurable results and ongoing support",
    icon: "StarIcon",
    metric: "98%"
  },
  {
    title: "Documented Cost Savings",
    description: "Proven ROI through operational optimization and revenue cycle improvements",
    icon: "CurrencyDollarIcon",
    metric: "$50M+"
  },
  {
    title: "Industry Experience",
    description: "Combined team expertise spanning all aspects of healthcare operations",
    icon: "AcademicCapIcon",
    metric: "200+ Years"
  }];


  const philosophyPillars: PhilosophyPillar[] = [
  {
    title: "Deep Industry Expertise",
    description: "We don't just consult on healthcare—we live it. Our team brings real-world clinical and operational experience.",
    icon: "AcademicCapIcon",
    principles: [
    "Every consultant has 10+ years healthcare industry experience",
    "Active medical credentials and certifications maintained",
    "Continuous education on regulatory changes and industry trends",
    "Real-world understanding of clinical workflows and constraints"]

  },
  {
    title: "Measurable Results Focus",
    description: "Success isn't subjective. We establish clear metrics and deliver documented improvements.",
    icon: "ChartBarIcon",
    principles: [
    "Baseline assessments with specific improvement targets",
    "Regular progress reporting with quantifiable metrics",
    "ROI documentation for every engagement",
    "Long-term sustainability planning beyond project completion"]

  },
  {
    title: "Compliance-First Approach",
    description: "Operational excellence means nothing without regulatory compliance. We build both simultaneously.",
    icon: "ShieldCheckIcon",
    principles: [
    "HIPAA compliance integrated into every recommendation",
    "Proactive risk identification and mitigation strategies",
    "Audit-ready documentation and process design",
    "Ongoing compliance monitoring and support"]

  },
  {
    title: "Partnership Mindset",
    description: "We succeed when you succeed. Our consultants become extensions of your team.",
    icon: "UserGroupIcon",
    principles: [
    "Collaborative approach respecting your organizational culture",
    "Knowledge transfer ensuring long-term capability building",
    "Accessible support beyond formal engagement periods",
    "Honest communication about challenges and opportunities"]

  }];


  if (!isHydrated) {
    return (
      <div data-component-id="src/app/about/components/AboutInteractive.tsx:225:6" data-component-path="src/app/about/components/AboutInteractive.tsx" data-component-line="225" data-component-end-line="233" data-component-file="AboutInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/about/components/AboutInteractive.tsx:226:8" data-component-path="src/app/about/components/AboutInteractive.tsx" data-component-line="226" data-component-end-line="232" data-component-file="AboutInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%22%7D" className="animate-pulse">
          <div data-component-id="src/app/about/components/AboutInteractive.tsx:227:10" data-component-path="src/app/about/components/AboutInteractive.tsx" data-component-line="227" data-component-end-line="227" data-component-file="AboutInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%22%7D" className="h-96 bg-muted"></div>
          <div data-component-id="src/app/about/components/AboutInteractive.tsx:228:10" data-component-path="src/app/about/components/AboutInteractive.tsx" data-component-line="228" data-component-end-line="231" data-component-file="AboutInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20py-16%20space-y-8%22%7D" className="max-w-7xl mx-auto px-4 py-16 space-y-8">
            <div data-component-id="src/app/about/components/AboutInteractive.tsx:229:12" data-component-path="src/app/about/components/AboutInteractive.tsx" data-component-line="229" data-component-end-line="229" data-component-file="AboutInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-64%20bg-muted%20rounded-lg%22%7D" className="h-64 bg-muted rounded-lg"></div>
            <div data-component-id="src/app/about/components/AboutInteractive.tsx:230:12" data-component-path="src/app/about/components/AboutInteractive.tsx" data-component-line="230" data-component-end-line="230" data-component-file="AboutInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-64%20bg-muted%20rounded-lg%22%7D" className="h-64 bg-muted rounded-lg"></div>
          </div>
        </div>
      </div>);

  }

  return (
    <div data-component-id="src/app/about/components/AboutInteractive.tsx:238:4" data-component-path="src/app/about/components/AboutInteractive.tsx" data-component-line="238" data-component-end-line="248" data-component-file="AboutInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <HeroSection data-component-id="src/app/about/components/AboutInteractive.tsx:239:6" data-component-path="src/app/about/components/AboutInteractive.tsx" data-component-line="239" data-component-end-line="239" data-component-file="AboutInteractive.tsx" data-component-name="HeroSection" data-component-content="%7B%22elementName%22%3A%22HeroSection%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" {...heroData} />
      <CompanyStory data-component-id="src/app/about/components/AboutInteractive.tsx:240:6" data-component-path="src/app/about/components/AboutInteractive.tsx" data-component-line="240" data-component-end-line="240" data-component-file="AboutInteractive.tsx" data-component-name="CompanyStory" data-component-content="%7B%22elementName%22%3A%22CompanyStory%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" {...companyData} />
      {/* <IndustryRecognition
        certifications={certifications}
        partnerships={partnerships}
        achievements={achievements} /> */}

      <ClientSuccessPhilosophy data-component-id="src/app/about/components/AboutInteractive.tsx:246:6" data-component-path="src/app/about/components/AboutInteractive.tsx" data-component-line="246" data-component-end-line="246" data-component-file="AboutInteractive.tsx" data-component-name="ClientSuccessPhilosophy" data-component-content="%7B%22elementName%22%3A%22ClientSuccessPhilosophy%22%7D" pillars={philosophyPillars} />
      <CTASection data-component-id="src/app/about/components/AboutInteractive.tsx:247:6" data-component-path="src/app/about/components/AboutInteractive.tsx" data-component-line="247" data-component-end-line="247" data-component-file="AboutInteractive.tsx" data-component-name="CTASection" data-component-content="%7B%22elementName%22%3A%22CTASection%22%7D" />
    </div>);

};

export default AboutInteractive;